import { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "./auth";

/**
 * Uniform "your session expired" recovery for any account-tied API call.
 *
 * Usage:
 *   const guard = useSessionGuard();
 *   try { await api.get("/me/orders"); }
 *   catch (err) {
 *     const handled = await guard(err, { retry: () => api.get("/me/orders") });
 *     if (!handled) toast.error(err?.response?.data?.detail || "…");
 *   }
 *
 * Behaviour:
 *  - Returns false (and does nothing) when the error is not a 401.
 *  - On 401: re-syncs auth via refreshMe(). If the user came back AND a
 *    `retry` callback was supplied, retries silently — caller never sees
 *    the error.
 *  - If auth is decisively gone, surfaces a single friendly toast with a
 *    one-click "Sign in" action that routes to /login?redirect=<currentPath>.
 *  - Returns true once the 401 has been handled (UI feedback shown OR
 *    silent retry succeeded), so callers can skip their generic error path.
 *
 * Optional second arg:
 *  - `retry`: () => Promise — re-runs the original call after refresh.
 *  - `redirectTo`: string — overrides the default redirect path
 *    (defaults to the current location.pathname).
 *  - `message`: string — custom toast message.
 */
export function useSessionGuard() {
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(async (err, opts = {}) => {
    if (err?.response?.status !== 401) return false;
    const { retry, redirectTo, message } = opts;

    const fresh = await refreshMe?.();
    if (fresh && typeof retry === "function") {
      try {
        await retry();
        return true;
      } catch (retryErr) {
        // If the retry also 401s, fall through to the toast path below.
        if (retryErr?.response?.status !== 401) throw retryErr;
      }
    }

    const target = redirectTo || location.pathname || "/";
    toast.error(message || "Session expired — please sign in again to continue.", {
      action: {
        label: "Sign in",
        onClick: () => navigate(`/login?redirect=${encodeURIComponent(target)}`),
      },
    });
    return true;
  }, [refreshMe, navigate, location.pathname]);
}
